import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Coffee, MessagesSquare, Megaphone, ArrowRight, Loader2, X, Heart } from "lucide-react";
import PaymentMethods, { hasPaymentMethods } from "@/components/PaymentMethods";
import { api, formatApiError } from "@/lib/api";
import { absoluteImageUrl } from "@/components/ImageUploader";
import Seo from "@/components/Seo";
import CoffeeBackground from "@/components/CoffeeBackground";

const SERVICES = [
  {
    key: "consultation",
    icon: MessagesSquare,
    color: "lavender",
    bg: "bg-[var(--tint-peach)]",
    accent: "text-[var(--coral)]",
    label: "Discuție la o cafea",
    tagline: "O discuție caldă, de la om la om — despre orice ai pe suflet.",
    description:
      "Despre viața de adult, relații și despărțiri, începuturi și alegeri, prieteniile care se schimbă, banii care nu ajung, diminețile grele. Nu trebuie să fie despre nimic anume. Fără judecăți, cu cafea proaspătă.",
    tiers: [
      {
        lookup_key: "consultation_30min",
        title: "Discuție scurtă",
        duration: "30 de minute",
        price: "5 lei",
        blurb: "Numai bună pentru o întrebare mare — sau un dialog despre orice vrei tu.",
      },
      {
        lookup_key: "consultation_60min",
        title: "Discuție pe îndelete",
        duration: "60 de minute",
        price: "10 lei",
        blurb: "Timp pentru context, poveste și un gând limpede la final.",
      },
    ],
  },
  {
    key: "sponsor",
    icon: Megaphone,
    color: "yellow",
    bg: "bg-[var(--tint-butter)]",
    accent: "text-[var(--sun)]",
    label: "Sponsorizare pe blog",
    tagline: "Un produs pe care chiar l-aș folosi.",
    description:
      "Scriu despre el cu grijă și onestitate, în tonul firesc al blogului. Și îmi păstrez dreptul de a spune „nu, nu se potrivește”.",
    tiers: [
      {
        lookup_key: "sponsor_mention",
        title: "Mențiune într-o însemnare",
        duration: "produsul tău apare firesc",
        price: "10 lei",
        blurb: "O mențiune caldă, într-o însemnare deja planificată.",
      },
      {
        lookup_key: "sponsor_dedicated",
        title: "Însemnare dedicată",
        duration: "un articol întreg",
        price: "15 lei",
        blurb: "O însemnare întreagă, construită în jurul produsului, în tonul meu.",
      },
    ],
  },
];

const BuyerModal = ({ tier, manual, methods, onClose }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Am nevoie de numele și e-mailul tău.");
      return;
    }
    setSubmitting(true);
    try {
      if (manual) {
        const { data } = await api.post("/requests", {
          lookup_key: tier.lookup_key,
          name: name.trim(),
          email: email.trim(),
          message: message.trim(),
        });
        setDone(data);
        setSubmitting(false);
        return;
      }
      const { data } = await api.post("/payments/checkout", {
        lookup_key: tier.lookup_key,
        origin_url: window.location.origin,
        buyer_name: name.trim(),
        buyer_email: email.trim(),
        buyer_message: message.trim(),
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err, "Nu am putut deschide plata"));
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="buyer-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[color-mix(in_srgb,var(--milk)_40%,transparent)] p-4 backdrop-blur-sm"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-md rounded-3xl bg-[#2B1B13] p-8 ring-1 ring-[color-mix(in_srgb,var(--milk)_18%,transparent)]">
        <button
          data-testid="modal-close"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-2 text-[#F7F1E8]/68 hover:bg-slate-100"
          aria-label="Închide"
        >
          <X className="h-4 w-4" strokeWidth={1.6} />
        </button>
        <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)]">{tier.title}</p>
        <h3 className="mt-2 font-serif text-3xl font-light text-[#F7F1E8]">{tier.price}</h3>
        <p className="mt-2 text-sm text-[#F7F1E8]/78">{tier.blurb}</p>

        {done ? (
          <div data-testid="request-done" className="mt-6 space-y-4">
            <p className="font-serif text-xl text-[#F7F1E8]">Mulțumesc, {name.trim()}! Ți-am scris pe e-mail.</p>
            <p className="text-sm text-[#F7F1E8]/78">
              Îți răspund în 1-2 zile. Când îți e la îndemână, poți trimite suma prin oricare variantă de mai jos — scrie-ți numele în mesaj, ca să știu de la cine vine.
            </p>
            <PaymentMethods methods={methods} amount={done.amount_lei} compact />
            <button type="button" onClick={onClose} data-testid="request-close" className="w-full rounded-full border border-[color-mix(in_srgb,var(--milk)_20%,transparent)] px-6 py-3 text-xs font-medium uppercase tracking-[0.2em] text-[var(--milk)]">
              Închide
            </button>
          </div>
        ) : (
        <form onSubmit={submit} className="mt-6 space-y-3">
          <input
            data-testid="buyer-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Numele tău"
            className="w-full rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#0D0907] px-4 py-3 text-sm outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--tint-pink)]"
            required
            maxLength={80}
          />
          <input
            data-testid="buyer-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="E-mailul tău"
            className="w-full rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#0D0907] px-4 py-3 text-sm outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--tint-pink)]"
            required
          />
          <textarea
            data-testid="buyer-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Spune-mi în câteva rânduri ce ai vrea să discutăm / ce produs ai vrea să sponsorizezi…"
            rows={4}
            maxLength={1200}
            className="w-full rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#0D0907] px-4 py-3 text-sm outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--tint-pink)]"
          />
          <button
            type="submit"
            disabled={submitting}
            data-testid="buyer-submit"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--accent)] px-6 py-3 text-xs font-medium uppercase tracking-[0.2em] text-white transition-transform hover:-translate-y-0.5 active:scale-95 disabled:opacity-60"
          >
            {submitting ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.6} /> {manual ? "Se trimite…" : "Se deschide plata…"}
              </>
            ) : (
              <>
                {manual ? "Trimite mesajul" : "Continuă la plată"} <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.6} />
              </>
            )}
          </button>
          <p className="text-center text-[11px] text-[#F7F1E8]/58">
            {manual ? "Primești pe e-mail variantele de plată (Revolut, transfer bancar…). Fără card, fără cont." : "Plată securizată prin Stripe · Card test: 4242 4242 4242 4242"}
          </p>
        </form>
        )}
      </div>
    </div>
  );
};

export const SponsorPage = () => {
  const [selectedTier, setSelectedTier] = useState(null);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api.get("/site-settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const priceFor = (tier) => {
    const lei = settings.prices?.[tier.lookup_key];
    return lei ? `${lei} lei` : tier.price;
  };
  const manual = (settings.payments_mode || "manual") !== "stripe";
  const showFree = manual && hasPaymentMethods(settings);

  return (
    <main data-testid="sponsor-page" className="relative overflow-hidden">
      <CoffeeBackground slot="questions" testId="questions-bg" />
      <div className="relative mx-auto max-w-5xl px-6 py-20">
      <Seo title="Cumpără-mi o cafea" description="Susține blogul La Cafea, Fără Menajamente: discuții calde de la om la om — despre viață, relații și alegeri — și colaborări oneste cu Alexia." />
      <div className="max-w-2xl">
        <div className="inline-flex items-center gap-2 rounded-full bg-[var(--tint-pink)] px-4 py-1.5 text-xs uppercase tracking-[0.25em] text-[var(--accent)]">
          <Coffee className="h-3.5 w-3.5" strokeWidth={1.6} />
          Cumpără-mi o cafea
        </div>
        <h1 className="mt-6 font-serif text-5xl font-light leading-tight text-[#F7F1E8] sm:text-6xl">
          Hai să stăm <span className="italic text-[var(--coral)]">la o cafea</span>.
        </h1>
        <p className="mt-6 text-lg font-light leading-relaxed text-[#F7F1E8]/86">
          Dacă vrei o discuție liniștită — despre viață, relații, alegeri sau orice te frământă —
          sau ai un produs care crezi că mi s-ar potrivi și l-aș recomanda cu drag, aici e locul potrivit.
          Alege o variantă și lasă-mi câteva rânduri despre ce ai în minte.
        </p>
      </div>

      {settings.sponsor_image && (
        <div className="mt-12 overflow-hidden rounded-[var(--frame,1.5rem)] shadow-lg shadow-black/10" data-testid="sponsor-image">
          <img src={settings.sponsor_image.startsWith("http") ? settings.sponsor_image : settings.sponsor_image} alt="" className="h-auto max-h-[420px] w-full object-cover" loading="lazy" />
        </div>
      )}

      <div className="mt-16 space-y-16">
        {SERVICES.map((svc, si) => {
          const Icon = svc.icon;
          return (
            <React.Fragment key={svc.key}>
              {si === 1 && settings.sponsor_image_2 && (
                <div className="overflow-hidden rounded-[var(--frame,1.5rem)] shadow-lg shadow-black/10" data-testid="sponsor-image-2">
                  <img src={absoluteImageUrl(settings.sponsor_image_2)} alt="Poză aleasă de Alexia" className="h-auto max-h-[380px] w-full object-cover" loading="lazy" />
                </div>
              )}
              <section
                data-testid={`service-${svc.key}`}
                className={`rounded-3xl ${svc.bg} p-8 sm:p-12`}
              >
              <div className="flex flex-wrap items-start justify-between gap-6">
                <div className="max-w-xl">
                  <div className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] ${svc.accent}`}>
                    <Icon className="h-4 w-4" strokeWidth={1.6} />
                    {svc.tagline}
                  </div>
                  <h2 className="mt-3 font-serif text-3xl font-medium text-[#F7F1E8] sm:text-4xl">{svc.label}</h2>
                  <p className="mt-4 text-base font-light leading-relaxed text-[#F7F1E8]/86">{svc.description}</p>
                </div>
              </div>

              <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
                {svc.tiers.map((tier) => (
                  <button
                    key={tier.lookup_key}
                    data-testid={`tier-${tier.lookup_key}`}
                    onClick={() => setSelectedTier({ ...tier, price: priceFor(tier) })}
                    className="group flex flex-col rounded-2xl bg-[#2B1B13] p-6 text-left ring-1 ring-[color-mix(in_srgb,var(--milk)_18%,transparent)] transition-all hover:-translate-y-1 hover:ring-[#F7F1E8]/30"
                  >
                    <p className="text-[11px] uppercase tracking-[0.28em] text-[#F7F1E8]/68">{tier.duration}</p>
                    <p className="mt-1 font-serif text-xl font-medium text-[#F7F1E8]">{tier.title}</p>
                    <p className="mt-2 text-sm text-[#F7F1E8]/78">{tier.blurb}</p>
                    <div className="mt-6 flex items-baseline justify-between">
                      <span className="font-serif text-3xl font-medium text-[#F7F1E8]">{priceFor(tier)}</span>
                      <span className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78 group-hover:text-[#F7F1E8]">
                        Alege <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.6} />
                      </span>
                    </div>
                  </button>
                ))}
              </div>
              </section>
            </React.Fragment>
          );
        })}
      </div>

      {settings.sponsor_image_3 && (
        <div className="mt-16 overflow-hidden rounded-[var(--frame,1.5rem)] shadow-lg shadow-black/10" data-testid="sponsor-image-3">
          <img src={absoluteImageUrl(settings.sponsor_image_3)} alt="Poză aleasă de Alexia" className="h-auto max-h-[380px] w-full object-cover" loading="lazy" />
        </div>
      )}

      {showFree && (
        <section data-testid="free-support" className="mt-16 rounded-3xl bg-[var(--tint-butter)] p-8 sm:p-12">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[var(--sun-dark)]">
            <Heart className="h-4 w-4" strokeWidth={1.6} /> Doar o cafea, fără nimic în schimb
          </div>
          <h2 className="mt-3 font-serif text-3xl font-medium text-[#F7F1E8] sm:text-4xl">Vrei doar să-mi faci ziua mai bună?</h2>
          <p className="mt-4 max-w-xl text-base font-light leading-relaxed text-[#F7F1E8]/86">
            Orice sumă, oricât de mică, înseamnă o cafea băută la laptop și încă o însemnare scrisă fără grabă. Mulțumesc din inimă.
          </p>
          <div className="mt-8">
            <PaymentMethods methods={settings} />
          </div>
        </section>
      )}

      <p className="mt-16 max-w-2xl text-sm text-[#F7F1E8]/68">
        Nota mea sinceră: nu accept orice. Dacă produsul nu se potrivește cu ce cred și scriu aici, îți returnez suma
        integral și rămânem prieteni.
      </p>

      {selectedTier && <BuyerModal tier={selectedTier} manual={manual} methods={settings} onClose={() => setSelectedTier(null)} />}
      </div>
    </main>
  );
};

export default SponsorPage;
